import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, CheckCircle2, Target, CalendarClock, Mail, User, ArrowUpRight, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const fmtDate = (d) => (d ? new Date(d).toISOString().split("T")[0] : "—");

function AccuracyRing({ value = 0 }) {
  const v = Math.max(0, Math.min(100, value));
  const R = 44;
  const C = 2 * Math.PI * R;
  const offset = C - (v / 100) * C;
  return (
    <div className="relative w-28 h-28 shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={R} stroke="#f4f4f5" strokeWidth="8" fill="none" />
        <circle cx="50" cy="50" r={R} stroke="url(#dw-grad)" strokeWidth="8" fill="none"
          strokeLinecap="round" strokeDasharray={C} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(.22,.61,.36,1)" }} />
        <defs>
          <linearGradient id="dw-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0b1f3a" />
            <stop offset="100%" stopColor="#d4af37" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display text-2xl font-bold tracking-tight" data-testid="overall-acc">{v.toFixed(1)}<span className="text-base">%</span></div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-0.5">Overall</div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="group relative rounded-2xl bg-white border border-[var(--line)] p-5 shadow-[0_1px_2px_rgba(10,10,10,0.04)] hover:shadow-[0_10px_30px_-12px_rgba(10,10,10,0.18)] hover:-translate-y-0.5 transition-all duration-300">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent === "red" ? "bg-rose-50 text-[var(--accent)]" : "bg-zinc-100 text-[var(--ink)]"} group-hover:bg-[var(--ink)] group-hover:text-white transition-colors`}>
          <Icon size={18} />
        </div>
        <ArrowUpRight size={14} className="text-zinc-300 group-hover:text-[var(--ink)] transition-colors" />
      </div>
      <div className="font-display text-3xl font-bold mt-5 tracking-tight">{value}</div>
      <div className="text-xs uppercase tracking-[0.12em] text-zinc-500 mt-1">{label}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-2">{sub}</div>}
    </div>
  );
}

function accBadgeClass(acc) {
  if (acc >= 95) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (acc >= 80) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-rose-50 text-rose-700 border-rose-200";
}

export default function CandidateResults() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [shown, setShown] = useState(12);

  useEffect(() => { (async () => { const r = await api.get("/candidate/results"); setData(r.data); })(); }, []);

  if (!data) return <div className="text-zinc-500">{t("loading")}</div>;
  if (!data.show_result) return (
    <div className="rounded-2xl bg-white border border-[var(--line)] p-10 text-center max-w-lg mx-auto">
      <h2 className="font-display text-2xl font-bold">{lang === "hi" ? "परिणाम छिपे हैं" : "Results hidden"}</h2>
      <p className="text-zinc-500 mt-2 text-sm">{lang === "hi" ? "एडमिन ने परिणाम देखने को अक्षम कर दिया है।" : "Your admin has disabled result viewing."}</p>
    </div>
  );

  const e = data.employee || {};
  const subs = data.submissions || [];
  const visible = subs.slice(0, shown);
  const percentComplete = e.total_pages ? Math.round((e.completed_pages / e.total_pages) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{lang === "hi" ? "मेरा डैशबोर्ड" : "My Dashboard"}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{lang === "hi" ? "परिणाम" : "Result Summary"}</h1>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--ink)] text-white text-xs font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-pulse" />
          {lang === "hi" ? "लाइव" : "Live"}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileText} label={lang === "hi" ? "कुल पेज" : "Total Pages"} value={e.total_pages || 0} sub={`Range ${e.from_page}–${e.to_page}`} />
        <StatCard icon={CheckCircle2} label={lang === "hi" ? "पूर्ण पेज" : "Completed Pages"} value={e.completed_pages || 0} sub={`${percentComplete}% done`} />
        <StatCard icon={Target} label={lang === "hi" ? "कुल सटीकता" : "Overall Accuracy"} value={`${data.total_accuracy}%`} sub={`${data.total_pages_100_acc || 0} pages @ 100%`} accent="red" />
        <StatCard icon={CalendarClock} label={lang === "hi" ? "अंतिम सबमिशन" : "Last Submission"} value={data.last_submission_date || "—"} sub={data.last_submission_date ? "Most recent activity" : "No submissions yet"} />
      </div>

      {/* Profile + Accuracy ring */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-[var(--line)] p-6 shadow-[0_1px_2px_rgba(10,10,10,0.04)]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[var(--ink)] text-white font-display text-lg font-bold flex items-center justify-center shrink-0">
              {(e.first_name || "?").charAt(0)}{(e.last_name || "").charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-xl font-bold truncate">{e.first_name} {e.last_name}</div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500 mt-1">
                <span className="inline-flex items-center gap-1"><Mail size={12} /> {e.email}</span>
                <span className="inline-flex items-center gap-1"><User size={12} /> {lang === "hi" ? "कैंडिडेट" : "Candidate"}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-5 border-t border-[var(--line)]">
            {[
              [lang === "hi" ? "आरंभ" : "Start Date", fmtDate(e.start_date)],
              [lang === "hi" ? "अंतिम तिथि" : "Last Date", fmtDate(e.last_date)],
              [lang === "hi" ? "पेज रेंज" : "Page Range", `${e.from_page}–${e.to_page}`],
              ["100% Pages", data.total_pages_100_acc || 0],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k}</div>
                <div className="text-sm font-semibold mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[var(--line)] p-6 shadow-[0_1px_2px_rgba(10,10,10,0.04)] flex items-center gap-5">
          <AccuracyRing value={data.total_accuracy} />
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">{lang === "hi" ? "कुल प्रदर्शन" : "Overall Performance"}</div>
            <div className="font-display text-lg font-bold mt-1">
              {data.total_accuracy >= 50 ? (lang === "hi" ? "अच्छा" : "On Track") : (lang === "hi" ? "सुधार की ज़रूरत" : "Needs Focus")}
            </div>
            <div className="inline-flex items-center gap-1 text-xs text-zinc-500 mt-2">
              <TrendingUp size={12} className="text-[var(--accent)]" /> {subs.length} {lang === "hi" ? "सबमिशन" : "submissions"}
            </div>
          </div>
        </div>
      </div>

      {/* Page completion bar */}
      <div className="rounded-2xl bg-white border border-[var(--line)] p-5 shadow-[0_1px_2px_rgba(10,10,10,0.04)]">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">{lang === "hi" ? "असाइनमेंट प्रगति" : "Assignment Progress"}</div>
          <div className="text-sm font-mono text-zinc-600">{e.completed_pages || 0} / {e.total_pages || 0}</div>
        </div>
        <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[var(--ink)] to-[var(--accent)] transition-all duration-700"
            style={{ width: `${percentComplete}%` }} />
        </div>
      </div>

      {/* Results table */}
      <div className="rounded-2xl bg-white border border-[var(--line)] shadow-[0_1px_2px_rgba(10,10,10,0.04)] overflow-hidden">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-[var(--line)]">
          <div>
            <div className="font-display text-lg font-bold">{lang === "hi" ? "पेज-वार परिणाम" : "Page-wise Results"}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{subs.length} {lang === "hi" ? "कुल सबमिशन" : "total submissions"}</div>
          </div>
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-zinc-50/95 backdrop-blur">
              <tr>
                <th className="text-left px-5 sm:px-6 py-3 text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">Page</th>
                <th className="text-left px-5 sm:px-6 py-3 text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">Accuracy</th>
                <th className="text-left px-5 sm:px-6 py-3 text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold hidden sm:table-cell">Submitted</th>
                <th className="text-right px-5 sm:px-6 py-3 text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => (
                <tr key={s.id} className="border-t border-[var(--line)] hover:bg-zinc-50/70 transition-colors">
                  <td className="px-5 sm:px-6 py-4 font-medium">{s.page_name}</td>
                  <td className="px-5 sm:px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${accBadgeClass(s.accuracy || 0)}`}>
                      {(s.accuracy || 0).toFixed(2)}%
                    </span>
                  </td>
                  <td className="px-5 sm:px-6 py-4 text-zinc-500 text-xs hidden sm:table-cell">{fmtDate(s.submitted_at)}</td>
                  <td className="px-5 sm:px-6 py-4 text-right">
                    <Link to={`/dashboard/results/${s.id}`} data-testid={`cand-view-${s.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[var(--ink)] text-white text-xs font-semibold hover:bg-[var(--accent)] transition-colors">
                      Check Result <ArrowUpRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
              {!visible.length && (
                <tr><td colSpan={4} className="px-6 py-14 text-center text-zinc-500 text-sm">No submissions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {subs.length > shown && (
          <div className="px-5 sm:px-6 py-4 border-t border-[var(--line)] flex justify-center">
            <button onClick={() => setShown((n) => n + 12)} data-testid="cand-load-more"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-[var(--line)] text-xs font-semibold hover:border-[var(--ink)] hover:bg-zinc-50 transition-colors">
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
